import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { MailCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function VerifyOtp() {
  const { verifyOtp, resendOtp } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => { if (!email) navigate("/login"); }, [email, navigate]);
  useEffect(() => {
    if (cooldown <= 0) return;
    const tmr = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(tmr);
  }, [cooldown]);

  const submit = async (value) => {
    const finalCode = value || code;
    if (finalCode.length !== 6) return;
    setLoading(true);
    try {
      const data = await verifyOtp(email, finalCode);
      toast.success("Email verified!");
      const role = data.user?.role;
      navigate(role === "admin" ? "/admin" : role === "seller" ? "/seller" : "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
      setCode("");
    }
    setLoading(false);
  };

  const resend = async () => {
    try {
      const data = await resendOtp(email);
      if (data.already_verified) { toast.success("Already verified"); return navigate("/"); }
      toast.success("A new code has been sent to your email.");
      setCooldown(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend");
      setCooldown(60);
    }
  };

  return (
    <Layout hideAd>
      <div className="max-w-md mx-auto px-4 py-16 text-center">
        <MailCheck className="w-14 h-14 mx-auto text-primary mb-4" />
        <h1 className="font-display font-bold text-2xl">{t("verifyEmail")}</h1>
        <p className="text-sm text-muted-foreground mt-2">{t("sentCode")} <span className="font-semibold text-foreground">{email}</span></p>
        <div className="mt-8 flex flex-col items-center gap-6">
          <InputOTP maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) submit(v); }} data-testid="otp-input">
            <InputOTPGroup>
              {[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} data-testid={`otp-slot-${i}`} />)}
            </InputOTPGroup>
          </InputOTP>
          <Button data-testid="verify-btn" onClick={() => submit()} disabled={loading || code.length !== 6} size="lg" className="w-full rounded-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("verify")}
          </Button>
          <button data-testid="resend-otp-btn" onClick={resend} disabled={cooldown > 0} className="text-sm text-primary font-semibold hover:underline disabled:opacity-50 disabled:no-underline">
            {cooldown > 0 ? `${t("resendCode")} (${cooldown}s)` : t("resendCode")}
          </button>
        </div>
      </div>
    </Layout>
  );
}
